import { memo } from "react";

function fmtMoney(n) {
  return `£${Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function StatCard({ label, value, sub, color, children }) {
  return (
    <div style={{
      flex: 1, minWidth: 145, padding: "0.9rem 1rem",
      background: "#fff", border: "1px solid #35454C",
      borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        fontSize: "0.72rem", fontWeight: 700, color: "#aaa",
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.3rem",
      }}>
        {label}
      </div>
      <div style={{ fontSize: "1.35rem", fontWeight: 900, color: color || "#0C2D48" }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: "0.75rem", color: "#aaa", marginTop: "0.15rem" }}>{sub}</div>
      )}
      {children}
    </div>
  );
}

/**
 * ProjectFinancialSummary
 *
 * Props:
 *   totalInvoiced  — sum of all invoice totals for this project (number, GBP)
 *   totalReceived  — sum of paid invoice totals only (number, GBP)
 *   totalExpenses  — sum of all expense amounts for this project (number, GBP)
 *   budget         — project.budget (number or 0 / falsy if unset)
 */
function ProjectFinancialSummary({ totalInvoiced, totalReceived, totalExpenses, budget }) {
  const netProfit = totalReceived - totalExpenses;
  const isProfit = netProfit >= 0;

  const hasBudget = budget > 0;
  const budgetRemaining = hasBudget ? budget - totalExpenses : null;
  const budgetPct = hasBudget ? Math.min(100, (totalExpenses / budget) * 100) : 0;
  const overBudget = hasBudget && totalExpenses > budget;

  return (
    <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1.5rem" }}>

      <StatCard
        label="Total invoiced"
        value={fmtMoney(totalInvoiced)}
        sub="All invoices"
        color="#0C2D48"
      />

      <StatCard
        label="Total received"
        value={fmtMoney(totalReceived)}
        sub="Paid invoices"
        color="#1e6645"
      />

      <StatCard
        label="Total expenses"
        value={fmtMoney(totalExpenses)}
        sub="All project expenses"
        color="#7a4a10"
      />

      <StatCard
        label="Net profit"
        value={fmtMoney(netProfit)}
        sub="Received minus expenses"
        color={isProfit ? "#1e6645" : "#A12710"}
      />

      {hasBudget && (
        <StatCard
          label="Budget remaining"
          value={fmtMoney(budgetRemaining)}
          sub={
            overBudget
              ? `${fmtMoney(totalExpenses - budget)} over budget`
              : `${budgetPct.toFixed(0)}% of budget used`
          }
          color={overBudget ? "#A12710" : budgetPct > 80 ? "#7a4a10" : "#0C2D48"}
        >
          <div style={{
            marginTop: "0.5rem", background: "#f0f4f8",
            borderRadius: 4, height: 6, overflow: "hidden",
          }}>
            <div style={{
              height: "100%", borderRadius: 4,
              background: overBudget ? "#A12710" : budgetPct > 80 ? "#FBC490" : "#6abf85",
              width: `${budgetPct}%`,
              transition: "width 0.3s",
            }} />
          </div>
        </StatCard>
      )}
    </div>
  );
}

export default memo(ProjectFinancialSummary);
